import { FC, useEffect, useRef, useState } from 'react'

import { RenderIf } from 'components/RenderIf'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { qrMenuGET } from 'featureSliced/shared/api/qrMenu/GET'
import {
  IAllergen,
  IProductInfo,
} from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { Allergens } from 'featureSliced/shared/hooks/openModals/useOpenProductModal/ComonProduct/Allergens'
import { Description } from 'featureSliced/shared/hooks/openModals/useOpenProductModal/ComonProduct/Description'

import { RichRuns } from './RichRuns'
import { NutrientTable } from './NutrientTable'
import { runsToText, shouldOfferExpand } from './declarationClamp'
import {
  DeclarationBlock,
  DeclarationFade,
  InfoSection,
  InfoSectionTitle,
  ProductInfoContainer,
  SectionToggle,
  SectionToggleBody,
  StatusText,
  TextControl,
} from './styled'

type ProductInfoModalProps = {
  categoryId: number
  productId: number
  // false = the article has no declaration/nutrient item; render the catalogue
  // sections only and never ask the API (the item would be a guaranteed 404)
  fetchDetails?: boolean
  // sub-article sheet: allergens + note travel with the catalogue entry and are
  // rendered here; the top-level product shows them in the modal body instead
  withCatalogueSections?: boolean
  // QRM-662: the web inline body shares the dialog with the add-to-cart footer,
  // so it opens clamped. The phone sheet and the menu-component block are opened
  // on purpose and show everything.
  collapsible?: boolean
  allergens?: IAllergen[]
  allergenLabels?: string[]
  note?: string | null
}

export const ProductInfoModal: FC<ProductInfoModalProps> = ({
  categoryId,
  productId,
  fetchDetails = true,
  withCatalogueSections = false,
  collapsible = false,
  allergens,
  allergenLabels,
  note,
}) => {
  const t = useTranslations()

  const [info, setInfo] = useState<IProductInfo | null>(null)
  const [isLoading, setIsLoading] = useState(fetchDetails)
  const [isError, setIsError] = useState(false)
  const [isDeclarationOpen, setIsDeclarationOpen] = useState(false)
  const [areNutrientsOpen, setAreNutrientsOpen] = useState(false)
  const [measuredOverflow, setMeasuredOverflow] = useState<boolean | null>(null)
  const declarationRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    if (!fetchDetails) return undefined
    let isMounted = true
    qrMenuGET
      .productInfo(categoryId, productId)
      .then((data) => {
        if (!isMounted) return
        setInfo(data)
        setIsLoading(false)
      })
      .catch(() => {
        if (!isMounted) return
        setIsError(true)
        setIsLoading(false)
      })
    return () => {
      isMounted = false
    }
  }, [categoryId, productId, fetchDetails])

  // Measured only while the box is still clamped, and never again once it is
  // open: expanding removes the clamp, so scrollHeight would equal clientHeight
  // and the control would unmount itself, leaving no way to collapse again.
  // The declaration arrives from an async fetch and the tenant font is injected
  // at runtime, so one layout pass is not enough — hence fonts.ready + observer.
  useEffect(() => {
    const node = declarationRef.current
    if (!collapsible || isDeclarationOpen || !node) return undefined

    // a box that is not laid out (hidden, zero width) reports 0/0, which would
    // read as "fits" and take the control away; leave the flag unmeasured so the
    // length fallback decides
    const measure = () =>
      setMeasuredOverflow(
        node.clientHeight > 0 ? node.scrollHeight > node.clientHeight + 1 : null
      )
    measure()

    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(node)

    const { fonts } = document as Document & { fonts?: FontFaceSet }
    fonts?.ready?.then(measure).catch(() => undefined)

    return () => observer?.disconnect()
  }, [collapsible, isDeclarationOpen, info])

  const declaration = info?.declaration || []
  const canExpandDeclaration =
    collapsible &&
    shouldOfferExpand({
      measured: measuredOverflow,
      text: runsToText(declaration),
    })

  const sections = [
    {
      key: 'allergens',
      // the block renders its own heading
      title: null,
      isVisible: withCatalogueSections && Boolean(allergens?.length),
      content: (
        <Allergens
          product={{
            allergens: allergens || [],
            allergenLabels: allergenLabels || [],
          }}
        />
      ),
    },
    {
      key: 'ingredients',
      title: t('productInfo.ingredients'),
      isVisible: Boolean(declaration.length),
      content: (
        <>
          <DeclarationBlock>
            <RichRuns
              ref={declarationRef}
              runs={declaration}
              clamped={collapsible && !isDeclarationOpen}
            />
            <RenderIf condition={canExpandDeclaration && !isDeclarationOpen}>
              <DeclarationFade />
            </RenderIf>
          </DeclarationBlock>
          <RenderIf condition={canExpandDeclaration}>
            <TextControl
              type="button"
              data-testid="declaration_toggle"
              aria-expanded={isDeclarationOpen}
              onClick={() => setIsDeclarationOpen((prev) => !prev)}
            >
              {isDeclarationOpen
                ? t('productInfo.showLess')
                : t('productInfo.showMore')}
            </TextControl>
          </RenderIf>
        </>
      ),
    },
    {
      key: 'nutrients',
      title: collapsible ? null : t('productInfo.nutrients'),
      isVisible: Boolean(info?.nutrients?.length),
      content: collapsible ? (
        <>
          <SectionToggle
            type="button"
            data-testid="nutrients_toggle"
            aria-expanded={areNutrientsOpen}
            $isOpen={areNutrientsOpen}
            onClick={() => setAreNutrientsOpen((prev) => !prev)}
          >
            {t('productInfo.nutrients')}
          </SectionToggle>
          <RenderIf condition={areNutrientsOpen}>
            <SectionToggleBody>
              <NutrientTable rows={info?.nutrients || []} />
            </SectionToggleBody>
          </RenderIf>
        </>
      ) : (
        <NutrientTable rows={info?.nutrients || []} />
      ),
    },
    {
      key: 'note',
      title: null,
      isVisible: withCatalogueSections && Boolean(note),
      content: <Description note={note ?? undefined} />,
    },
  ]

  return (
    <ProductInfoContainer data-testid="product_info_modal">
      <RenderIf condition={isLoading}>
        <StatusText>{t('common.loading')}</StatusText>
      </RenderIf>
      <RenderIf condition={isError}>
        <StatusText>{t('productInfo.loadError')}</StatusText>
      </RenderIf>
      {sections
        .filter((section) => section.isVisible)
        .map((section) => (
          <InfoSection key={section.key}>
            <RenderIf condition={section.title !== null}>
              <InfoSectionTitle>{section.title}</InfoSectionTitle>
            </RenderIf>
            {section.content}
          </InfoSection>
        ))}
    </ProductInfoContainer>
  )
}
